import type { LabelElement, LabelTemplate } from '@trackeat/shared';
import { expiryWeekday } from './expiryWeekday.js';

export interface RenderedElement {
  id: string;
  type: LabelElement['type'];
  x_mm: number;
  y_mm: number;
  w_mm?: number;
  h_mm?: number;
  text?: string;
  barcode_data?: string;
  qr_data?: string;
  font_size_pt?: number;
  font_weight: 'normal' | 'bold';
  rotation: 0 | 90 | 180 | 270;
}

type FilterArg = string | undefined;
type FilterFn = (value: unknown, arg: FilterArg) => string;

const ALLERGEN_LABELS_IT: Record<string, string> = {
  gluten: 'glutine',
  crustaceans: 'crostacei',
  eggs: 'uova',
  fish: 'pesce',
  peanuts: 'arachidi',
  soybeans: 'soia',
  milk: 'latte',
  nuts: 'frutta a guscio',
  celery: 'sedano',
  mustard: 'senape',
  sesame: 'sesamo',
  sulphites: 'solfiti',
  lupin: 'lupini',
  molluscs: 'molluschi',
};

const pad2 = (n: number): string => (n < 10 ? `0${n}` : String(n));

const toDate = (value: unknown): Date | null => {
  if (value instanceof Date) return value;
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
};

const filters: Record<string, FilterFn> = {
  upper: (value) => stringify(value).toUpperCase(),
  lower: (value) => stringify(value).toLowerCase(),
  strip: (value, arg) => {
    const s = stringify(value);
    if (!arg) return s;
    const prefix = arg.replace(/^['"]|['"]$/g, '');
    return s.startsWith(prefix) ? s.slice(prefix.length) : s;
  },
  date: (value, arg) => {
    const d = toDate(value);
    if (!d) return '';
    const format = (arg ?? 'DD/MM/YYYY').replace(/^['"]|['"]$/g, '');
    const DD = pad2(d.getDate());
    const MM = pad2(d.getMonth() + 1);
    const YYYY = String(d.getFullYear());
    const YY = YYYY.slice(-2);
    const HH = pad2(d.getHours());
    const mm = pad2(d.getMinutes());
    return format
      .replace(/YYYY/g, YYYY)
      .replace(/YY/g, YY)
      .replace(/MM/g, MM)
      .replace(/DD/g, DD)
      .replace(/HH/g, HH)
      .replace(/mm/g, mm);
  },
  // T-303 (collettiva) — mostra un lp_code collettiva (formato interno
  // `LP-DDMMYYYY`, deterministico dalla data di produzione) come data leggibile
  // `DD/MM/YYYY`. Se il valore non è nel formato collettiva (es. lotto random
  // `LP-AB12CD34`) ritorna il codice senza il prefisso `LP-`: così il filtro è
  // sicuro su qualunque etichetta e può rimpiazzare `strip:LP-` nel layout del
  // cliente collettiva.
  lotdate: (value) => {
    const s = stringify(value);
    const m = /^LP-(\d{2})(\d{2})(\d{4})$/.exec(s);
    if (m) return `${m[1]}/${m[2]}/${m[3]}`;
    return s.startsWith('LP-') ? s.slice(3) : s;
  },
  allergens: (value) => {
    if (!Array.isArray(value)) return '';
    const parts = value
      .filter((v): v is string => typeof v === 'string')
      .map((key) => ALLERGEN_LABELS_IT[key] ?? key);
    if (parts.length === 0) return '';
    // Niente truncate: la larghezza la gestisce il box (width_mm) via wrap.
    return 'Allergeni: ' + parts.join(', ');
  },
  /**
   * `ingredients` — formatta lista nomi ingredienti per etichetta.
   * Input: string[] (nomi food). Output: "Ingr.: pollo, riso, carote".
   * Vuoto → stringa vuota. Il word-wrap viene fatto dal renderer
   * quando l'elemento testo ha width_mm settato (text-box).
   */
  ingredients: (value) => {
    if (!Array.isArray(value)) return '';
    const parts = value
      .filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
      .map((s) => s.toLowerCase().trim());
    if (parts.length === 0) return '';
    return 'Ingr.: ' + parts.join(', ');
  },
};

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  // NB: usiamo "..." (3 dot ASCII) non "…" (U+2026): il firmware TSPL della
  // Clabel non gestisce UTF-8 multi-byte e renderizzerebbe ideogrammi cinesi
  // al posto dell'ellipsis singolo.
  return s.slice(0, Math.max(0, max - 3)).trimEnd() + '...';
}

/**
 * Word-wrap di una stringa rispettando una larghezza massima espressa in
 * caratteri (derivata da widthMm e fontPt). Restituisce array di righe.
 * Se una parola è più lunga del width, la spezza con hard-break.
 */
export function wrapTextLines(text: string, charsPerLine: number, maxLines?: number): string[] {
  if (charsPerLine <= 0) return [text];
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let cur = '';
  for (const w of words) {
    if (!cur) {
      cur = w;
      continue;
    }
    if ((cur + ' ' + w).length <= charsPerLine) {
      cur += ' ' + w;
    } else {
      lines.push(cur);
      cur = w;
    }
    // hard break per parole troppo lunghe
    while (cur.length > charsPerLine) {
      lines.push(cur.slice(0, charsPerLine));
      cur = cur.slice(charsPerLine);
    }
  }
  if (cur) lines.push(cur);
  if (maxLines != null && lines.length > maxLines) {
    const truncated = lines.slice(0, maxLines);
    if (truncated.length > 0) {
      const last = truncated[truncated.length - 1] ?? '';
      truncated[truncated.length - 1] = (last.length > charsPerLine - 3
        ? last.slice(0, charsPerLine - 3)
        : last) + '...';
    }
    return truncated;
  }
  return lines;
}

// 203 DPI: 1 dot = 25.4/203 mm.
const DOT_MM = 25.4 / 203;

/**
 * Metrica REALE del font bitmap che verrà stampato. La testina Clabel usa
 * font ROM a celle fisse: il `font_size_pt` dell'editor NON scala il glifo,
 * viene solo mappato al font bitmap più vicino (vedi pickTsplFont in tspl.ts,
 * soglie qui rispecchiate). Conseguenze pratiche per l'utente:
 *  - qualsiasi pt ≤ 5 → font "0" (8×12 dot): cambiare 4→1 non rimpicciolisce
 *    nulla, è già il minimo assoluto.
 *  - per far stare più testo (es. tutti gli allergeni) si va a CAPO su più
 *    righe (box più alto), non si riduce il font.
 * Wrap e line-height DEVONO usare questa cella reale, altrimenti il renderer
 * (e l'anteprima) calcolano righe diverse da ciò che stampa la testina.
 */
function bitmapCellMm(fontPt: number | undefined): { charWmm: number; lineHmm: number } {
  const pt = fontPt ?? 7;
  // [larghezza, altezza] cella in dot — identico a FONT_CELL di tspl.ts.
  const cell: [number, number] =
    pt <= 5  ? [8, 12]  :
    pt <= 8  ? [12, 20] :
    pt <= 11 ? [16, 24] :
    pt <= 15 ? [24, 32] :
    pt <= 20 ? [32, 48] :
               [48, 72];
  return {
    charWmm: cell[0] * DOT_MM,
    lineHmm: cell[1] * DOT_MM * 1.15, // +15% interlinea
  };
}

/**
 * Caratteri-per-riga reali: larghezza box / larghezza cella del font bitmap
 * effettivamente stampato (NON l'approssimazione proporzionale sul pt).
 */
export function charsPerLineFor(widthMm: number, fontPt: number): number {
  if (widthMm <= 0) return 0;
  return Math.max(1, Math.floor(widthMm / bitmapCellMm(fontPt).charWmm));
}

/**
 * Altezza in mm di una riga del font bitmap effettivamente stampato.
 */
export function lineHeightMmFor(fontPt: number): number {
  return bitmapCellMm(fontPt).lineHmm;
}

const stringify = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (value instanceof Date) return value.toISOString();
  return JSON.stringify(value);
};

const resolvePath = (data: Record<string, unknown>, path: string): unknown => {
  const parts = path.split('.');
  let current: unknown = data;
  for (const part of parts) {
    if (current === null || current === undefined) return undefined;
    if (typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
};

const parseFilter = (raw: string): { name: string; arg: FilterArg } => {
  const colonIdx = raw.indexOf(':');
  if (colonIdx === -1) return { name: raw.trim(), arg: undefined };
  return {
    name: raw.slice(0, colonIdx).trim(),
    arg: raw.slice(colonIdx + 1).trim(),
  };
};

const BINDING_RE = /\{\{\s*([^}]+?)\s*\}\}/g;

export const resolveBinding = (
  binding: string,
  data: Record<string, unknown>,
): string => {
  return binding.replace(BINDING_RE, (_match, expr: string) => {
    const segments = expr.split('|').map((s) => s.trim());
    const pathPart = segments[0] ?? '';
    let value: unknown = resolvePath(data, pathPart);
    for (let i = 1; i < segments.length; i += 1) {
      const { name, arg } = parseFilter(segments[i]!);
      const fn = filters[name];
      if (fn) value = fn(value, arg);
    }
    return stringify(value);
  });
};

const resolveElementText = (
  element: LabelElement,
  data: Record<string, unknown>,
): string => {
  if (element.binding) return resolveBinding(element.binding, data);
  if (element.static_value) return element.static_value;
  return '';
};

export const renderElements = (
  elements: LabelElement[],
  data: Record<string, unknown>,
): RenderedElement[] => {
  const out: RenderedElement[] = [];
  for (const el of elements) {
    if (el.type === 'qr' && el.qr_format === 'rmqr') {
      throw new Error('rMQR richiede un driver raster: TSPL, Niimbot, sistema, QZ o anteprima.');
    }
    const rendered: RenderedElement = {
      id: el.id,
      type: el.type,
      x_mm: el.x_mm,
      y_mm: el.y_mm,
      w_mm: el.width_mm ?? undefined,
      h_mm: el.height_mm ?? undefined,
      font_size_pt: el.font_size_pt ?? undefined,
      font_weight: el.font_weight ?? 'normal',
      rotation: (el.rotation ?? 0) as 0 | 90 | 180 | 270,
    };
    const value = resolveElementText(el, data);
    switch (el.type) {
      case 'expiry_weekday': {
        const day = expiryWeekday(data.expires_at);
        if (!day) continue;
        rendered.text = day;
        rendered.w_mm = el.width_mm ?? 12;
        rendered.h_mm = el.height_mm ?? 12;
        break;
      }
      case 'text': {
        if (value === '') continue;
        // Text-box con larghezza: applica word-wrap e (opzionalmente) clipping
        // verticale. Senza width_mm il testo è una singola riga come prima.
        if (el.width_mm && el.width_mm > 0) {
          const fontPt = el.font_size_pt ?? 8;
          const cpl = charsPerLineFor(el.width_mm, fontPt);
          const lh = lineHeightMmFor(fontPt);
          const maxLines = el.height_mm && el.height_mm > 0
            ? Math.max(1, Math.floor(el.height_mm / lh))
            : undefined;
          const lines = wrapTextLines(value, cpl, maxLines);
          for (let i = 0; i < lines.length; i += 1) {
            out.push({
              ...rendered,
              id: i === 0 ? rendered.id : `${rendered.id}__l${i}`,
              y_mm: rendered.y_mm + i * lh,
              text: lines[i] ?? '',
            });
          }
          continue;
        }
        rendered.text = value;
        break;
      }
      case 'barcode':
        rendered.barcode_data = value;
        break;
      case 'qr':
        rendered.qr_data = value;
        break;
      default:
        break;
    }
    out.push(rendered);
  }
  return out;
};

export const renderTemplate = (
  template: LabelTemplate,
  data: Record<string, unknown>,
): RenderedElement[] => renderElements(template.elements, data);
